<template>
	<PanelDraggable :data="data" :draggable="isDraggable">
		<div>

			<!-- HEADER -->
			<Button v-if="dashboards.editMode" size="small" severity="danger" class="dash-delete-btn" @click="remove()">
				<GlyphIcon :icon="GlyphIconName['bin']" color="#fff" size="18" />
			</Button>
			<div class="dash-header" @mouseover="mouseOverHeader(true)" @mouseout="mouseOverHeader(false)" :editMode="dashboards.editMode">
				<GlyphIcon :icon="GlyphIconName['user']" color="#fff" size="18" style="margin-top:2px" />
				<div class="dotdotdot" style="margin-left:3px">{{ props.data.title }}<span v-if="collapsed">&nbsp;({{ extensions.length }})</span></div>
				<div v-show="!dashboards.editMode" class="flex flex-row items-start gap-1.5 justify-between">
					<Button size="small" variant="text" class="header-button" @click="toggleCollapse()">
						<GlyphIcon :icon="GlyphIconName['chevron-up']" size="19" class="transition-transform" :class="{ 'upside-down': collapsed }" />
					</Button>
				</div>
			</div>

			<!-- EDIT MODE -->
			<div v-if="dashboards.editMode" class="dash-settings-box">
				<div class="w-full mb-3">
					<div>{{ t('extensions.panelName') }}</div>
					<InputText v-model="settings.title" size="small" fluid :required="true" maxlength="64" />
				</div>

				<div class="w-full mb-3">
					<div>{{ t('extensions.selectedEmployees') }}</div>
					<div class="list-box">
						<Draggable :list="extensions" item-key="_id" @change="extensionsDragChange" class="list-group" ghost-class="ghost" :group="'extensions' + props.data.column +'-' + props.data.position">
							<template #item="{ element }">
								<div class="flex justify-between items-center employee-item" @dblclick="removeExtension(element)">
									<div class="employee-item-left">
										<Button size="small" severity="danger" class="delete-employee-btn shrink-0" @click="removeExtension(element)">
											<GlyphIcon :icon="GlyphIconName['bin']" size="15" />
										</Button>
										<GlyphIcon v-if="(element as Employee).type === PbxAppEmployeesType.EXTENSION" :icon="GlyphIconName['phone-old']" size="13" class="typeIcon" />
										<GlyphIcon v-if="(element as Employee).type === PbxAppEmployeesType.EMPLOYEE" :icon="GlyphIconName['user']" size="13" class="typeIcon" user />
										<div class="title">{{ (element as Employee).name }}</div>
									</div>
									<div class="shrink-0 -mt-0.5">
										<Badge variant="primary" class="employee-pill">{{ (element as Employee).primaryExtension?.number }}</Badge>
									</div>
								</div>
							</template>
						</Draggable>
						<div class="text-center italic text-muted-color text-sm" v-if="extensions.length === 0">{{ t('common.noneSelected') }}</div>
					</div>
				</div>

				<div class="w-full mb-3">
					<div>{{ t('extensions.availableEmployees') }}</div>
					<div style="position:relative">
						<GlyphIcon :icon="GlyphIconName['search']" size="20" class="searchIcon" />
					</div>
					<InputText type="search" size="small" fluid v-model="filter" :placeholder="t('common.filter')" class="searchInput" />
					<!-- <div style="max-height:155px;overflow:scroll"> -->
					<div class="list-box with-search" style="max-height:173px;overflow:scroll">
						<Draggable :list="availableExtensionsFiltered" item-key="_id" @move="availableExtensionsMove" @change="availableExtensionsDragChange" class="list-group" ghost-class="ghost" :group="'extensions' + props.data.column +'-' + props.data.position">
							<template #item="{ element }">
								<div class="flex justify-between items-center employee-item" @dblclick="addExtension(element)">
									<div class="employee-item-left">
										<Button size="small" severity="success" class="delete-employee-btn shrink-0" @click="addExtension(element)">
											<GlyphIcon :icon="GlyphIconName['plus']" size="15" />
										</Button>
										<GlyphIcon v-if="(element as Employee).type === PbxAppEmployeesType.EXTENSION" :icon="GlyphIconName['phone-old']" size="13" class="typeIcon" />
										<GlyphIcon v-if="(element as Employee).type === PbxAppEmployeesType.EMPLOYEE" :icon="GlyphIconName['user']" size="13" class="typeIcon" user />
										<div class="title">{{ (element as Employee).name }}</div>
									</div>
									<div class="shrink-0 -mt-0.5">
										<Badge variant="primary" class="employee-pill">{{ (element as Employee).primaryExtension?.number }}</Badge>
									</div>
								</div>
							</template>
						</Draggable>
						<div class="text-center italic text-muted-color text-sm" v-if="availableExtensionsFiltered.length === 0">{{ t('common.noneAvailable') }}</div>
					</div>
				</div>

			</div>

			<!-- VIEW MODE -->
			<div v-if="!dashboards.editMode" class="employee-container" :style="{'max-height': collapsed ? '0px': (extensions.length * 60) + 'px'}">
				<ExtensionsEmployee :employee-id="item._id" v-for="(item,index) in extensions" :key="item._id" :last="extensions.length - 1 === index" />
			</div>
		</div>
	</PanelDraggable>
</template>

<script setup lang="ts">
import GlyphIcon from '@/components/GlyphIcon.vue'
import { useDashboardsStore } from '@/stores/Dashboards'
import { useSettingsStore } from '@/stores/Settings'
import { useEmployeesStore } from '@/stores/Employees'
import { useI18n } from 'vue-i18n'
import Draggable from 'vuedraggable'
import PanelDraggable from '@/components/dashboard/PanelDraggable.vue'
import ExtensionsEmployee from '@/components/dashboard/ExtensionsEmployee.vue'
import globalEvents from '@/utilities/GlobalEvents'
import { type PbxAppDashboardItemExtensions, PbxAppEmployeesType } from '@/TcxApi/PbxApp'
import { ref, computed, watch } from 'vue'
import Employee from '@/stores/Employee'
import { dynamicSort, getRegExFromFilter, sleep } from '@/utilities/Tools'
import type { DraggeableChangeEventExtensions } from '@/definitions/Common'
import { GlyphIconName } from '@/stores/GlyphIcons.types'

const dashboards = useDashboardsStore()
const ss = useSettingsStore()
const employees = useEmployeesStore()
const { t } = useI18n()

interface Props {
	data: PbxAppDashboardItemExtensions
}

const props = defineProps<Props>()
const settings = dashboards.dashboard?.items.find(item => item === props.data) as PbxAppDashboardItemExtensions

const extensions = ref<Employee[]>([])
const availableExtensions = ref<Employee[]>([])
const filter = ref('')
const isDraggable = ref(false)
const isLoading = ref(false)
const collapsed = ref<boolean>(ss.getGeneric(`${ss.settings.dashboardId}-${props.data.type}-${props.data.column}-${props.data.position}-collapsed`) ?? false)

const availableExtensionsFiltered = computed(() => {
	if (!filter.value) { return availableExtensions.value }

	return availableExtensions.value.filter(ext => {
		const search = [ext.name, ext.title, ext.department, ext.emailAddress]

		if (ext.address) { search.push(ext.address.address, ext.address.city, ext.address.zip) }
		ext.extensions.forEach(ext => { search.push(ext.number); search.push(ext.direct); search.push(...ext.mobile) })
		const rx = getRegExFromFilter(filter.value)

		return rx.test(search.filter(item => item).map(item => item?.toLowerCase()).join(' '))
	})
})

function extensionsDragChange(event: DraggeableChangeEventExtensions) {
	if (event.moved) {
		settings.extensions.splice(event.moved.newIndex, 0, settings.extensions.splice(event.moved.oldIndex, 1)[0] as string)
	} else if (event.added) {
		settings.extensions.splice(event.added.newIndex, 0, event.added.element._id)
		// | console.log(`added to extensions at ${event.added.newIndex}: ${event.added.element.name}`) // eslint-disable-line
	} else if (event.removed) {
		settings.extensions.splice(event.removed.oldIndex, 1)
		// | console.log(`removed from extensions at ${event.removed.oldIndex}: ${event.removed.element.name}`) // eslint-disable-line
	}
}

function availableExtensionsDragChange(event: DraggeableChangeEventExtensions) {
	if (event.moved || event.added || event.removed) {
		availableExtensions.value.sort(dynamicSort('name'))
	}
}

function availableExtensionsMove(_event: DraggeableChangeEventExtensions) {
	// console.dir(event) // eslint-disable-line
	return false
}

function remove() {
	dashboards.removeItem(props.data.column, props.data.position)
}

function addExtension(item: Employee) {
	extensions.value.push(item)
	const index = availableExtensions.value.findIndex(ext => ext._id === item._id)

	availableExtensions.value.splice(index, 1)
	settings.extensions.push(item._id)
}

function removeExtension(item: Employee) {
	availableExtensions.value.push(item)
	availableExtensions.value.sort(dynamicSort('name'))
	const index = extensions.value.findIndex(ext => ext._id == item._id)

	extensions.value.splice(index, 1)
	settings.extensions.splice(index, 1)
}

function mouseOverHeader(itIs: boolean) {
	if (itIs && dashboards.editMode && !dashboards.dragItem) {
		isDraggable.value = true
	} else {
		isDraggable.value = false
	}
}

async function loadEmployees() {
	if (isLoading.value) { return }
	isLoading.value = true
	if (extensions.value.length > 0) {
		extensions.value = []
		await sleep(1000)
	}

	// Load the list of available employess and populate the list of selected employees and if in edit mode also the available employees list
	for (const employeeId of props.data.extensions) {
		const employee = employees.getEmployee(employeeId)
		if (employee) { extensions.value.push(employee as unknown as Employee) }
	}

	if (dashboards.editMode) {
		availableExtensions.value = employees.employees.filter(employee => !props.data.extensions.includes(employee._id)).sort(dynamicSort('name'))
	}

	isLoading.value = false
}

function toggleCollapse() {
	collapsed.value = !collapsed.value
	ss.setGeneric(`${ss.settings.dashboardId}-${props.data.type}-${props.data.column}-${props.data.position}-collapsed`, collapsed.value)
}

globalEvents.on('Employees:Loaded', () => { loadEmployees() })
loadEmployees()

watch(() => dashboards.editMode, (newValue) => {
	if (newValue) {
		availableExtensions.value = employees.employees.filter(employee => !props.data.extensions.includes(employee._id)).sort(dynamicSort('name'))
	} else {
		availableExtensions.value = []
		filter.value = ''
	}
})
</script>

<style scoped>
	.employee-container {
		margin: 0;
		/* padding-bottom: 2px;
		padding-top: 2px; */
		display: flex;
		flex-direction: column;
		overflow: hidden;
		transition: 400ms;
	}
	.delete-employee-btn {
		padding: 0 2px 0 2px;
		margin-right: 5px;
		box-shadow: none !important;
	}
	.title {
		font-size: 13px;
		overflow: hidden;
		margin-right: 5px;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.ghost {
		opacity: 0;
	}
	.employee-pill {
		font-size: 11px;
		font-weight: 500;
		min-width: 40px;
		height: 19px;
	}
	.employee-item {
		padding: 3px 5px 3px 5px !important;
		cursor: move;
	}
	.employee-item:not(:last-child) {
		border-bottom: 1px solid var(--p-form-field-border-color);
	}
	.employee-item-left {
		display: flex;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space:nowrap;
	}
	.list-box {
		border-radius: 5px;
		background-color: var(--p-surface-200);
		border: 1px solid var(--p-form-field-border-color);
	}
	.list-box.with-search {
		border-top: 0;
		border-top-left-radius: 0;
		border-top-right-radius: 0;
	}
	@media(prefers-color-scheme: dark) {
		.list-box {
			background-color: var(--p-surface-800);
		}
	}
	.typeIcon {
		margin-top: 2px;
		margin-right: 1px;
		opacity: 0.4;
	}
	.typeIcon[user] {
		margin-top: 3px;
	}
	.searchIcon {
		position: absolute;
		padding-left: 4px;
		padding-top: 6px;
		opacity:0.5;
	}
	.searchInput {
		padding-left: 25px;
		border-bottom-left-radius: 0;
		border-bottom-right-radius: 0;
	}
</style>
